'use client';

import { Stack, Typography } from '@mui/material';
import type { AnomalyGraphData } from '@repo/api-gw-sdk';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Dot,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { dayjs } from '@/utils/dayjs';

interface AnomalyChartProps {
  graphData: AnomalyGraphData;
  label: string;
}

export const AnomalyChart = ({ graphData, label }: AnomalyChartProps) => {
  const dataPoints = graphData.dataPoints ?? [];
  if (dataPoints.length === 0) return null;

  const chartData = dataPoints.map((dp) => ({
    timestamp: dayjs.utc(dp.timestamp).format('MMM D'),
    actual: dp.actualValue,
    forecast: dp.forecastValue,
    upperBound: dp.upperBound,
    lowerBound: dp.lowerBound,
    isAnomaly: dp.isAnomaly,
    // Delta for stacked area band rendering
    boundDelta: dp.upperBound - dp.lowerBound,
  }));

  return (
    <Stack gap='8px' paddingY='12px'>
      <Typography variant='subtitle2' fontWeight={600}>
        {label}
      </Typography>
      <ResponsiveContainer width='100%' height={200}>
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke='var(--mui-palette-divider)' />
          <XAxis
            dataKey='timestamp'
            tickLine={false}
            axisLine={false}
            fontSize={11}
            tickMargin={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            fontSize={11}
            tickCount={5}
            domain={['auto', 'auto']}
          />
          <Tooltip
            content={(props) => {
              const payload = props.payload?.[0]?.payload;
              if (!payload) return null;
              return (
                <Stack
                  sx={(theme) => ({
                    boxShadow: theme.customShadows.xs,
                    borderColor: theme.palette.divider,
                    background: theme.palette.background.paper,
                    borderRadius: '4px',
                    padding: '8px 12px',
                  })}
                  gap='4px'
                >
                  <Typography fontSize={12} fontWeight={600}>
                    {payload.timestamp}
                  </Typography>
                  <Typography fontSize={12}>
                    Actual: {Number(payload.actual).toFixed(1)}
                  </Typography>
                  <Typography fontSize={12}>
                    Forecast: {Number(payload.forecast).toFixed(1)}
                  </Typography>
                  {payload.isAnomaly && (
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color='var(--mui-palette-error-main)'
                    >
                      Anomaly detected
                    </Typography>
                  )}
                </Stack>
              );
            }}
          />
          {/* Lower bound base (invisible, anchors the stacked band) */}
          <Area
            type='monotone'
            dataKey='lowerBound'
            stackId='band'
            stroke='none'
            fill='transparent'
            isAnimationActive={false}
          />
          {/* Upper-lower delta (shaded band between bounds) */}
          <Area
            type='monotone'
            dataKey='boundDelta'
            stackId='band'
            stroke='none'
            fill='var(--mui-palette-action-hover)'
            fillOpacity={0.4}
            isAnimationActive={false}
          />
          {/* Forecast line (dotted) */}
          <Line
            type='monotone'
            dataKey='forecast'
            stroke='var(--mui-palette-text-disabled)'
            strokeWidth={1.5}
            strokeDasharray='5 5'
            dot={false}
            isAnimationActive={false}
          />
          {/* Actual data line (solid) */}
          <Line
            type='monotone'
            dataKey='actual'
            stroke='var(--mui-palette-warning-main)'
            strokeWidth={2}
            dot={(dotProps) => {
              const { cx, cy, payload } = dotProps;
              if (payload?.isAnomaly) {
                return (
                  <Dot
                    key={`anomaly-${cx}-${cy}`}
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill='var(--mui-palette-error-main)'
                    stroke='var(--mui-palette-error-main)'
                  />
                );
              }
              return <Dot key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={0} />;
            }}
            activeDot={(dotProps) => {
              const { cx, cy, payload } = dotProps;
              return (
                <Dot
                  key={`active-${cx}-${cy}`}
                  cx={cx}
                  cy={cy}
                  r={payload?.isAnomaly ? 7 : 4}
                  fill={
                    payload?.isAnomaly
                      ? 'var(--mui-palette-error-main)'
                      : 'var(--mui-palette-warning-main)'
                  }
                  stroke={
                    payload?.isAnomaly
                      ? 'var(--mui-palette-error-main)'
                      : 'var(--mui-palette-warning-main)'
                  }
                />
              );
            }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Stack>
  );
};
